/**
 *  @class
 *  @function ImageTextOverlay
 */

if (!customElements.get('image-with-text-overlay')) {
  class ImageTextOverlay extends HTMLElement {
    constructor() {
      super();

      this.tl = false;
      this.splittext = false;
    }
    connectedCallback() {
      if (document.body.classList.contains('animations-true') && typeof gsap !== 'undefined') {
        this.prepareAnimations();
      }
      this.prepareScrollZoom();
      this.preparePurplePearlHero();
      this.preparePurplePearlMediaMotion();
      this.preparePurplePearlVideo();
    }
    disconnectedCallback() {
      if (document.body.classList.contains('animations-true') && typeof gsap !== 'undefined') {
        if (this.tl) {
          this.tl.kill();
        }
        if (this.splittext) {
          this.splittext.revert();
        }
      }
      if (this.scrollZoomHandler) {
        window.removeEventListener('scroll', this.scrollZoomHandler);
        window.removeEventListener('resize', this.scrollZoomHandler);
      }
      if (this.scrollZoomFrame) {
        window.cancelAnimationFrame(this.scrollZoomFrame);
      }
      if (this.purplePearlHeroHandler) {
        window.removeEventListener('scroll', this.purplePearlHeroHandler);
        window.removeEventListener('resize', this.purplePearlHeroHandler);
        document.body.classList.remove('purple-pearl-intro-active');
      }
      if (this.purplePearlHeroFrame) {
        window.cancelAnimationFrame(this.purplePearlHeroFrame);
      }
      if (this.purplePearlMediaMotionHandler) {
        window.removeEventListener('scroll', this.purplePearlMediaMotionHandler);
        window.removeEventListener('resize', this.purplePearlMediaMotionHandler);
      }
      if (this.purplePearlMediaMotionFrame) {
        window.cancelAnimationFrame(this.purplePearlMediaMotionFrame);
      }
      if (this.purplePearlMediaMotionQuery && this.purplePearlMediaMotionHandler) {
        this.purplePearlMediaMotionQuery.removeEventListener('change', this.purplePearlMediaMotionHandler);
      }
      if (this.purplePearlVideoTimer) {
        window.clearTimeout(this.purplePearlVideoTimer);
      }
      if (this.purplePearlVideo) {
        this.purplePearlVideo.pause();
        this.purplePearlVideo.removeEventListener('canplay', this.purplePearlVideoReadyHandler);
        this.purplePearlVideo.removeEventListener('playing', this.purplePearlVideoPlayingHandler);
        this.purplePearlVideo.removeEventListener('error', this.purplePearlVideoErrorHandler);
      }
      if (this.purplePearlDesktopQuery && this.purplePearlVideoViewportHandler) {
        this.purplePearlDesktopQuery.removeEventListener('change', this.purplePearlVideoViewportHandler);
      }
      if (this.purplePearlMotionQuery && this.purplePearlVideoViewportHandler) {
        this.purplePearlMotionQuery.removeEventListener('change', this.purplePearlVideoViewportHandler);
      }
    }
    preparePurplePearlHero() {
      if (!this.classList.contains('purple-pearl-hero')) return;

      const heroStage = this.closest('.purple-pearl-hero-stage');
      const mobileViewport = window.matchMedia('(max-width: 767px)');

      this.purplePearlHeroHandler = () => {
        if (this.purplePearlHeroFrame) return;

        this.purplePearlHeroFrame = window.requestAnimationFrame(() => {
          if (!mobileViewport.matches || !heroStage) {
            document.body.classList.remove('purple-pearl-intro-active');
            this.purplePearlHeroFrame = null;
            return;
          }

          const header = document.querySelector('.header-section');
          const headerHeight = header ? header.offsetHeight : 0;
          const stageTop = heroStage.getBoundingClientRect().top + window.scrollY;
          const heroIntro = heroStage.nextElementSibling;
          const introTop = heroIntro && heroIntro.classList.contains('purple-pearl-hero__intro')
            ? heroIntro.getBoundingClientRect().top + window.scrollY
            : stageTop + heroStage.offsetHeight;
          const introHasReachedHeader = window.scrollY >= introTop - headerHeight;

          document.body.classList.toggle('purple-pearl-intro-active', introHasReachedHeader);
          this.purplePearlHeroFrame = null;
        });
      };

      window.addEventListener('scroll', this.purplePearlHeroHandler, { passive: true });
      window.addEventListener('resize', this.purplePearlHeroHandler, { passive: true });
      this.purplePearlHeroHandler();
    }
    preparePurplePearlMediaMotion() {
      if (!this.classList.contains('purple-pearl-hero')) return;
      if (!document.body.matches('.template-collection-purple-pearl, .template-collection-black-pearl, .template-collection-pink-peach-pearl, .template-collection-gray-silver-pearl')) return;

      const heroStage = this.closest('.purple-pearl-hero-stage');
      if (!heroStage) return;

      this.purplePearlMediaMotionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
      this.purplePearlMediaMotionHandler = () => {
        if (this.purplePearlMediaMotionFrame) return;

        this.purplePearlMediaMotionFrame = window.requestAnimationFrame(() => {
          const stageTop = heroStage.getBoundingClientRect().top + window.scrollY;
          const travelDistance = Math.max(heroStage.offsetHeight - this.offsetHeight, 0);
          const mobile = window.matchMedia('(max-width: 767px)').matches;
          const intro = heroStage.nextElementSibling;
          const heroHasLeft = intro && intro.classList.contains('purple-pearl-hero__intro') && intro.getBoundingClientRect().top <= 0;
          document.body.classList.toggle('purple-pearl-chat-visible', mobile && heroHasLeft);
          this.style.transform = '';

          if (mobile || !document.body.classList.contains('template-collection-purple-pearl') || this.purplePearlMediaMotionQuery.matches) {
            this.style.setProperty('--purple-pearl-media-scale', '1');
            this.purplePearlMediaMotionFrame = null;
            return;
          }

          const motionDistance = Math.max(travelDistance, window.innerHeight);
          const progress = Math.min(Math.max((window.scrollY - stageTop) / motionDistance, 0), 1);
          const scale = 1 + (progress * 0.055);

          this.style.setProperty('--purple-pearl-media-scale', scale.toFixed(4));
          this.purplePearlMediaMotionFrame = null;
        });
      };

      window.addEventListener('scroll', this.purplePearlMediaMotionHandler, { passive: true });
      window.addEventListener('resize', this.purplePearlMediaMotionHandler, { passive: true });
      this.purplePearlMediaMotionQuery.addEventListener('change', this.purplePearlMediaMotionHandler);
      this.purplePearlMediaMotionHandler();
    }
    preparePurplePearlVideo() {
      if (!this.classList.contains('purple-pearl-hero')) return;

      const video = this.querySelector('.purple-pearl-hero__video');
      if (!video) return;

      const sources = Array.from(video.querySelectorAll('source[data-src]'));
      const fallbackImage = this.querySelector('.purple-pearl-hero__fallback-image');
      const connection = navigator.connection || navigator.mozConnection || navigator.webkitConnection;

      this.purplePearlVideo = video;
      this.purplePearlDesktopQuery = window.matchMedia('(min-width: 768px)');
      this.purplePearlMotionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');

      const videoIsAllowed = () => {
        return this.purplePearlDesktopQuery.matches &&
          !this.purplePearlMotionQuery.matches &&
          !(connection && connection.saveData);
      };

      const deactivateVideo = () => {
        video.pause();
        this.classList.remove('is-video-active');
      };

      this.purplePearlVideoPlayingHandler = () => {
        if (!videoIsAllowed()) return;

        if ('requestVideoFrameCallback' in video) {
          video.requestVideoFrameCallback(() => {
            if (videoIsAllowed() && !video.paused) this.classList.add('is-video-active');
          });
          return;
        }

        window.requestAnimationFrame(() => {
          window.requestAnimationFrame(() => {
            if (videoIsAllowed() && !video.paused) this.classList.add('is-video-active');
          });
        });
      };

      this.purplePearlVideoErrorHandler = () => {
        this.classList.remove('is-video-active');
      };

      this.purplePearlVideoReadyHandler = () => {
        if (!videoIsAllowed()) return;

        const playPromise = video.play();
        if (playPromise) {
          playPromise.catch(() => this.classList.remove('is-video-active'));
        }
      };

      this.purplePearlVideoViewportHandler = () => {
        if (!videoIsAllowed()) {
          deactivateVideo();
          return;
        }

        if (!video.dataset.sourcesLoaded) {
          sources.forEach((source) => {
            source.src = source.dataset.src;
          });
          video.dataset.sourcesLoaded = 'true';
          video.load();
        }

        if (video.readyState >= 3) {
          this.purplePearlVideoReadyHandler();
        }
      };

      video.muted = true;
      video.defaultMuted = true;
      video.playsInline = true;
      video.addEventListener('canplay', this.purplePearlVideoReadyHandler);
      video.addEventListener('playing', this.purplePearlVideoPlayingHandler);
      video.addEventListener('error', this.purplePearlVideoErrorHandler);
      this.purplePearlDesktopQuery.addEventListener('change', this.purplePearlVideoViewportHandler);
      this.purplePearlMotionQuery.addEventListener('change', this.purplePearlVideoViewportHandler);

      const startVideoAfterFallback = async () => {
        if (!fallbackImage) {
          this.purplePearlVideoViewportHandler();
          return;
        }

        if (!fallbackImage.complete) {
          await new Promise((resolve) => {
            fallbackImage.addEventListener('load', resolve, { once: true });
            fallbackImage.addEventListener('error', resolve, { once: true });
          });
        }

        if (fallbackImage.complete && fallbackImage.naturalWidth > 0 && fallbackImage.decode) {
          await fallbackImage.decode().catch(() => {});
        }

        this.purplePearlVideoViewportHandler();
      };

      startVideoAfterFallback();
    }
    prepareScrollZoom() {
      if (!this.classList.contains('scroll-zoom--true')) return;
      if (this.classList.contains('purple-pearl-hero')) return;
      if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

      this.scrollZoomHandler = () => {
        if (this.scrollZoomFrame) return;

        this.scrollZoomFrame = window.requestAnimationFrame(() => {
          const rect = this.getBoundingClientRect();
          const progress = Math.min(Math.max(-rect.top / rect.height, 0), 1);
          const scale = 1 + (progress * 0.06);

          this.style.setProperty('--scroll-zoom-scale', scale.toFixed(4));
          this.scrollZoomFrame = null;
        });
      };

      window.addEventListener('scroll', this.scrollZoomHandler, { passive: true });
      window.addEventListener('resize', this.scrollZoomHandler, { passive: true });
      this.scrollZoomHandler();
    }
    prepareAnimations() {
      let section = this,
        button_offset = 0,
        property = (gsap.getProperty("html", "--header-height") + gsap.getProperty("html", "--header-offset")) + 'px';

      section.tl = gsap.timeline({
        scrollTrigger: {
          trigger: section,
          start: "top center"
        }
      });

      document.fonts.ready.then(function () {
        section.splittext = new SplitText(section.querySelectorAll('.image-with-text-overlay--heading, p:not(.subheading)'), {
          type: 'lines, words',
          linesClass: 'line-child'
        });

        let subheading = section.querySelector('.subheading');
        if (subheading) {
          section.tl
            .fromTo(subheading, {
              opacity: 0
            }, {
              duration: 0.75,
              opacity: 0.6
            }, 0);

          button_offset += 0.5;
        }

        let heading = section.querySelector('.image-with-text-overlay--heading');
        if (heading) {
          let headingLines = section.querySelectorAll('.image-with-text-overlay--heading .line-child div');
          let h3_duration = 0.8 + ((headingLines.length - 1) * 0.08);
          section.tl
            .set(heading, {
              visibility: 'visible'
            }, 0)
            .from(headingLines, {
              duration: h3_duration,
              yPercent: '100',
              stagger: 0.08
            }, 0);
          button_offset += h3_duration;
        }

        let rteParagraphs = section.querySelectorAll('.rte p');
        if (rteParagraphs.length) {
          let rteLines = section.querySelectorAll('.rte p .line-child div');
          let p_duration = 0.8 + ((rteLines.length - 1) * 0.02);
          section.tl
            .set(rteParagraphs, {
              visibility: 'visible'
            }, 0)
            .from(rteLines, {
              duration: p_duration,
              yPercent: '100',
              stagger: 0.02
            }, 0);
          button_offset += p_duration;
        }

        let buttons = section.querySelectorAll('.button');
        if (buttons.length) {
          buttons.forEach((item, index) => {
            section.tl.fromTo(item, {
              autoAlpha: 0
            }, {
              duration: 0.5,
              autoAlpha: 1
            }, ((button_offset * 0.4) + index * 0.1));
          });
        }

      });

      let parallaxImage = section.querySelector('.thb-parallax-image');
      if (parallaxImage) {
        gsap.fromTo(parallaxImage, {
          y: '-8%'
        }, {
          y: '8%',
          ease: 'none',
          scrollTrigger: {
            trigger: section,
            scrub: 1,
            start: () => `top bottom`,
            end: () => `bottom top+=${property}`,
            onUpdate: () => {
              property = (gsap.getProperty("html", "--header-height") + gsap.getProperty("html", "--header-offset")) + 'px';
            }
          }
        });
      }
    }
  }
  customElements.define('image-with-text-overlay', ImageTextOverlay);
}
